import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useMergedRefs } from "shared/lib/useMergedRefs"
import { useAccessibilityMessage } from "shared/types/useAccessibilityMessage"

import { RoomRowCellType } from "widgets/product-position-table/hooks/useRoomRowCells"

import { ProductPositionTableCell } from "../../ProductPositionTableCell"
import { ProductPositionTableRowCollapseButton } from "../../ProductPositionTableRowCollapseButton"
import { ProductPositionTableRowWrapper } from "../../ProductPositionTableRowWrapper"
import { ProductPositionRowClarificationRequestCell } from "../cells/ProductPositionRowClarificationRequestCell"
import { ProductPositionRowMenuCell } from "../cells/ProductPositionRowMenuCell"
import { ProductPositionRowPositionCell } from "../cells/ProductPositionRowPositionCell"
import { ProductPositionRowRemarkCell } from "../cells/ProductPositionRowRemarkCell"
import { ProductPositionRowSelectionCell } from "../cells/ProductPositionRowSelectionCell"
import { ProductPositionTableRoomRowTitleCell } from "../cells/ProductPositionTableRoomRowTitleCell"
import { useProductPositionTableRoomRowViewModel } from "../hooks/useProductPositionTableRoomRowViewModel"

export const ProductPositionTableRoomRowView = React.memo(
  (props: ReturnType<typeof useProductPositionTableRoomRowViewModel>) => {
    const { t } = useTranslation()
    const {
      item,
      index,
      style,
      isSelected,
      isCollapsed,
      dragHandleRef,
      draggedElementID,
      isNextParent,
      isDragDisabled,
      rowElementProps,
      isFocused,
      position,
      onCollapse,
      isPending,
      getCellProps,
      canCollapse,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset,
      actions,
      rowRef,
      menuCellRef
    } = props

    const isSelection = columnPreset === "selection"

    useAccessibilityMessage(
      {
        id: "focused-project-room-info",
        text: t(
          isCollapsed
            ? "ProjectPage.Accessibility.RoomRowFocusCollapsed"
            : "ProjectPage.Accessibility.RoomRowFocusExpanded",
          { title: item.label, position }
        )
      },
      isFocused
    )

    const mergedRowRef = useMergedRefs(
      rowRef,
      rowElementProps?.ref,
      dragHandleRef
    )

    return (
      <ProductPositionTableRowWrapper
        {...rowElementProps}
        role="row"
        ref={mergedRowRef}
        index={index}
        style={style}
        isDragDisabled={isDragDisabled || !!draggedElementID}
        data-key="room-row"
        data-testid={`room-row__${item.id}`}
        data-rowtype={item.__typename}
        data-selected={isSelected}
        className={classNames(
          "room",
          rowElementProps?.className,
          isSelected ? "highlight" : undefined,
          draggedElementID ? "disable-hover" : undefined,
          isNextParent ? "drag-parent" : undefined
        )}
      >
        <div role="gridcell" aria-hidden className="table-row-cell left-space">
          <div className="project-table-row__select-indicator" />
        </div>
        {showSelectionCheckbox ? (
          <ProductPositionRowSelectionCell
            isSelected={isSelected}
            label={item.label ?? ""}
          />
        ) : null}
        <ProductPositionTableCell aria-hidden className="collapse-button">
          {canCollapse || isPending ? (
            <ProductPositionTableRowCollapseButton
              item={item}
              isCollapsed={isCollapsed}
              onCollapse={onCollapse}
              isLoading={isPending}
            />
          ) : null}
        </ProductPositionTableCell>
        <ProductPositionRowPositionCell
          {...getCellProps?.(RoomRowCellType.POSITION)}
          position={position}
        />
        {showClarificationRequestColumn ? (
          <ProductPositionRowClarificationRequestCell
            {...getCellProps?.(RoomRowCellType.CLARIFICATION_REQUEST)}
            hasClarificationRequest={item.hasClarificationRequest}
          />
        ) : null}
        <ProductPositionTableRoomRowTitleCell
          {...getCellProps?.(RoomRowCellType.NAME)}
          room={item}
        />
        {isSelection ? null : (
          <ProductPositionRowRemarkCell
            {...getCellProps?.(RoomRowCellType.REMARK)}
            remark={item.remark}
          />
        )}
        {isSelection ? null : (
          <ProductPositionRowMenuCell
            ref={menuCellRef}
            actions={actions}
            isFocused={isFocused}
            cellProps={getCellProps?.(RoomRowCellType.MENU)}
          />
        )}
        <div
          role="gridcell"
          aria-hidden
          className="table-row-cell right-space"
        />
      </ProductPositionTableRowWrapper>
    )
  }
)

ProductPositionTableRoomRowView.displayName = "ProductPositionTableRoomRowView"
